import { ComponentType, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpenTextIcon, GearSixIcon, LightbulbIcon, XIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@libs/zapi-ui/ds/components/dialog";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Textarea } from "@libs/zapi-ui/ds/components/textarea";
import { chapterIcons, defaultChapterIcon, EChapterIcon } from "@libs/zapi-ui/const";
import type { InstructionsModalProps } from "./InstructionsModal";

const PROJECT = "project-instructions";
const OUTLINE = "outline-instructions";

type TSection = { id: string; label: string; icon: ComponentType<{ className?: string }>; filled: boolean };

function NavItem({
  section,
  selected,
  filledLabel,
  onSelect
}: {
  section: TSection;
  selected: boolean;
  filledLabel: string;
  onSelect: () => void;
}) {
  const Icon = section.icon;
  return (
    <button
      type="button"
      aria-current={selected || undefined}
      onClick={onSelect}
      className="typo-label-md flex min-h-9 w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-start text-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50 aria-current:bg-background aria-current:shadow-xs aria-current:ring-1 aria-current:ring-foreground/10"
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">{section.label}</span>
      {section.filled && <span aria-label={filledLabel} className="size-1.5 shrink-0 rounded-full bg-primary" />}
    </button>
  );
}

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="typo-overline px-2.5 pb-1.5 text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

// `InstructionsModal` on the design system, for parts rebuilt on the design system: the
// sections on the start side, the chosen section's editor on the other.
export function InstructionsDialog({
  open,
  onClose,
  onSave,
  isSaving,
  chapters,
  projectInstructions,
  chaptersInstructions,
  presentationPrompt,
  onProjectInstructionsChange,
  onPresentationPromptChange,
  defaultSection = PROJECT,
}: InstructionsModalProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const [selected, setSelected] = useState(defaultSection);
  const chapter = chapters.find((candidate) => candidate.slug === selected);
  const chapterIcon = (name?: string | null) =>
    (name && chapterIcons[name as EChapterIcon]) || chapterIcons[defaultChapterIcon];

  const project: TSection = {
    id: PROJECT,
    label: t("instructionsDialog.projectTitle"),
    icon: LightbulbIcon,
    filled: !!projectInstructions.trim()
  };
  const outline: TSection = {
    id: OUTLINE,
    label: t("instructionsDialog.outlineTitle"),
    icon: BookOpenTextIcon,
    filled: !!presentationPrompt.trim()
  };
  const chapterSections: TSection[] = chapters.map((candidate) => ({
    id: candidate.slug,
    label: candidate.name,
    icon: chapterIcon(candidate.icon),
    filled: !!chaptersInstructions[candidate.slug]?.trim()
  }));

  const current =
    selected === PROJECT
      ? {
          title: t("instructionsDialog.projectTitle"),
          description: t("instructionsDialog.projectDescription"),
          placeholder: t("instructionsDialog.projectPlaceholder"),
          value: projectInstructions
        }
      : selected === OUTLINE
      ? {
          title: t("instructionsDialog.outlineTitle"),
          description: t("instructionsDialog.outlineDescription"),
          placeholder: t("instructionsDialog.outlinePlaceholder"),
          value: presentationPrompt
        }
      : {
          title: chapter?.name ?? t("instructionsDialog.chapterTitle"),
          description: t("instructionsDialog.chapterDescription"),
          placeholder: t("instructionsDialog.chapterPlaceholder", {
            name: chapter?.name ?? t("instructionsDialog.thisChapter")
          }),
          value: chaptersInstructions[selected] ?? ""
        };

  const change = (value: string) => {
    if (selected === PROJECT) onProjectInstructionsChange(value, chaptersInstructions);
    else if (selected === OUTLINE) onPresentationPromptChange(value);
    else onProjectInstructionsChange(projectInstructions, { ...chaptersInstructions, [selected]: value });
  };

  const filledLabel = t("instructionsDialog.hasInstructions");

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        closeLabel={t("common:close")}
        showCloseButton={false}
        className="flex h-[min(46rem,calc(100svh-2rem))] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl sm:flex-row"
      >
        <nav
          aria-label={t("instructionsDialog.sections")}
          className="flex max-h-56 shrink-0 flex-col border-b bg-muted/40 sm:max-h-none sm:w-72 sm:border-e sm:border-b-0"
        >
          <div className="flex h-16 shrink-0 items-center gap-2 border-b px-5">
            <GearSixIcon className="size-5 text-muted-foreground" />
            <DialogTitle className="typo-label-lg">{t("instructionsDialog.title")}</DialogTitle>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-3">
            <NavGroup label={t("instructionsDialog.general")}>
              <NavItem
                section={project}
                selected={selected === PROJECT}
                filledLabel={filledLabel}
                onSelect={() => setSelected(PROJECT)}
              />
            </NavGroup>
            <NavGroup label={t("instructionsDialog.presentation")}>
              <NavItem
                section={outline}
                selected={selected === OUTLINE}
                filledLabel={filledLabel}
                onSelect={() => setSelected(OUTLINE)}
              />
            </NavGroup>
            {chapterSections.length > 0 && (
              <NavGroup label={t("instructionsDialog.chapters")}>
                {chapterSections.map((section) => (
                  <NavItem
                    key={section.id}
                    section={section}
                    selected={selected === section.id}
                    filledLabel={filledLabel}
                    onSelect={() => setSelected(section.id)}
                  />
                ))}
              </NavGroup>
            )}
          </div>
        </nav>
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="flex min-h-16 shrink-0 items-start gap-3 border-b px-6 py-3.5">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <h3 className="typo-label-lg truncate text-foreground">{current.title}</h3>
              <DialogDescription className="typo-caption text-muted-foreground">
                {current.description}
              </DialogDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              shape="pill"
              aria-label={t("instructionsDialog.close")}
              onClick={onClose}
            >
              <XIcon />
            </Button>
          </header>
          <div className="flex min-h-0 flex-1 p-6">
            <Textarea
              key={selected}
              value={current.value}
              onChange={(event) => change(event.target.value)}
              placeholder={current.placeholder}
              aria-label={current.title}
              className="typo-body-sm h-full min-h-40 resize-none field-sizing-fixed px-3.5 py-3"
            />
          </div>
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="ghost" shape="pill" onClick={onClose} disabled={isSaving}>
              {t("instructionsDialog.cancel")}
            </Button>
            <Button type="button" shape="pill" onClick={onSave ?? onClose} disabled={isSaving}>
              {isSaving && <Spinner data-icon="inline-start" aria-label={t("common:loading")} />}
              {isSaving ? t("instructionsDialog.saving") : t("instructionsDialog.save")}
            </Button>
          </footer>
        </section>
      </DialogContent>
    </Dialog>
  );
}
